import { html, nothing } from 'lit';
import '@awesome.me/webawesome/dist/components/button/button.js';
import { customElement, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';

import { nsSettingsServiceContext } from '../../../../../ns-services/contexts.js';
import { NSSettingsService, SettingsObject } from '../../../../../ns-settings/ns-settings-service.js';
import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import NSJobComponent from '../../../job.js';
import { isRegistrar } from '../../../../../helpers/registrars.js';
import { jobErrorStore } from '../../../../../ns-services/ns-job-error-store.js';

import JobSchema from '../../../../../models/job-schema.js';
import Company, { CompaniesRegistrar } from '../../../../../models/company.js';

import '../../../../../ns-elements/ns-job-layout.js';
import { NSHmrcTrustScoreStep, renderScoreBar, renderScoreInfo } from './trust-score-step.js';
import { WORDING, canRescore, reviewDeadline } from './trust-score-logic.js';
import definition from '@job-schemas/company/hmrc/trust-score-review.json';
import { jobSchema } from '../../../job-schema.js';

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * The review card the hmrc/trust-score-review cron raises 24-30 days after a company's HMRC score: the assignee (or a
 * verified director) runs the score again through hmrc/trust-score, reconnecting HMRC when asked, and the card completes
 * its job on OK. Left open for 14 days, the company's HMRC score falls to 0.
 */
@customElement(NSCompanyHMRCTrustScoreReview.is)
export default class NSCompanyHMRCTrustScoreReview extends NSJobComponent {
  static get is() {
    return 'ns-company-hmrc-trust-score-review';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  @query('ns-hmrc-trust-score-step')
  private __scoreStep?: NSHmrcTrustScoreStep;

  @state()
  private __ready = false;

  @state()
  private __allowed = false;

  @state()
  private __hmrcEntry: CompaniesRegistrar | null = null;

  @state()
  private __appMode: string | null = null;

  @state()
  private __notMtdRecorded = false;

  // The score is stored but the job's completion failed: the bar offers Complete again
  @state()
  private __completionFailed = false;

  private __completing = false;

  // This card completed its job while open: the step's answer stays on show
  @state()
  private __completedHere = false;

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;
    if (!this._job) return;

    try {
      await this._directory?.initialised;
      this.__allowed = canRescore(this._directory?.me, this._job);
      const app = this.__settings?.settings.app as SettingsObject | undefined;
      this.__appMode = typeof app?.mode === 'string' ? app.mode : null;
      // Back from a Reconnect that HMRC declined or failed (view-oauth put the reason in the job's error store)
      const returned = jobErrorStore.get<{ message?: unknown }>(this._job.id);
      if (returned && typeof returned.message === 'string') this.showError(returned.message);
      // HMRC answered "not authorised" at the last run: start at the two possibilities
      this.__notMtdRecorded = !!this._job.metadata.find((m) => m.key === 'HMRC_NOT_MTD')?.value;
      const company = await this.loadJobCompany<Company & { id: string }>();
      this.__hmrcEntry = company.companiesRegistrar?.find((entry) => isRegistrar(entry, 'HMRC')) ?? null;
    } catch (err) {
      this.showError(err, 'Unable to load this review. Please try again.');
    } finally {
      this.__ready = true;
    }
  }

  private async __scoreComplete() {
    if (this.__completing) return;
    this.__completing = true;
    this.__completionFailed = false;
    try {
      // Set first: the job can read COMPLETE before outcome() returns, and the step must not be dropped meanwhile
      this.__completedHere = true;
      await this.outcome('complete');
    } catch (err) {
      this.__completedHere = false;
      this.__completionFailed = true;
      this.showError(err, 'This card could not be completed. Please try again.');
    } finally {
      this.__completing = false;
    }
  }

  private __renderDeadline() {
    const deadline = reviewDeadline(this._job?.createdAt);
    if (!deadline) return nothing;
    return html`
      <p>${`${WORDING.reviewDeadlinePrefix} ${dateFormat.format(deadline)}, ${WORDING.reviewDeadlineSuffix}`}</p>
    `;
  }

  private __renderStep() {
    const complete = this._job?.status === 'COMPLETE';
    if (!this.__ready || (complete && !this.__completedHere)) return nothing;
    if (!this.__allowed) {
      return html`
        <div class="job-panel">
          <p role="alert">${WORDING.reviewNotAllowed}</p>
        </div>
      `;
    }
    return html`
      <ns-hmrc-trust-score-step
        kind="review"
        .jobId=${this._job?.id ?? ''}
        .companyId=${this._job?.companyId ?? ''}
        .provenId=${this.__hmrcEntry?.provenId ?? null}
        .environment=${this.__hmrcEntry?.environment ?? null}
        .appMode=${this.__appMode}
        ?not-mtd-recorded=${this.__notMtdRecorded}
        ?completed=${complete}
        @score-state-change=${() => this.requestUpdate()}
      ></ns-hmrc-trust-score-step>
    `;
  }

  render() {
    return html`
      <ns-job-layout>
        <div class="job-panel">
          <p>${WORDING.review}</p>
          ${this.__renderDeadline()}
        </div>
        ${this.__renderStep()} ${renderScoreInfo()}
      </ns-job-layout>
    `;
  }

  renderActions() {
    if (!this.__allowed || this._job?.status === 'COMPLETE') return nothing;
    if (this.__completionFailed) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.__scoreComplete()}>Complete</wa-button>
      `;
    }
    return renderScoreBar(this.__scoreStep, () => this.__scoreComplete(), { completing: this.__completing });
  }
}
